import dayjs from 'dayjs';
import type { AllPlanScheduleType, RoutineActivity, ScheduledAction } from '@demind-inc/core';

import type { SuggestAllAIPlanState } from '../data-access/recoil/suggestAllAIPlan.atom';
import {
  dedupeAllPlanScheduledActions,
  resolveAllPlanCacheDateKey,
  updatePlanCacheForDate,
} from './allPlanCache.helpers';
import { scheduleFallsOnDate } from './allPlanScheduleDate.helpers';

export { scheduleFallsOnDate } from './allPlanScheduleDate.helpers';

const getScheduleId = (schedule: ScheduledAction, fallbackIndex = 0): string =>
  String(schedule.id ?? `plan-${fallbackIndex}`);

const collectScheduleIds = (schedules: ScheduledAction[]): Set<string> =>
  new Set(schedules.map((schedule, index) => getScheduleId(schedule, index)));

const resolveAllPlanScheduleType = (schedule: ScheduledAction): string | undefined => {
  if (schedule.eventFrom === 'activity') {
    if (schedule.routineFrom?.routineActivityId) {
      return 'routine';
    }
    if (schedule.todoFrom?.taskId) {
      return 'todo';
    }
    return 'calendar';
  }

  if (schedule.eventFrom === 'existing_calendar') {
    return 'calendar';
  }

  return schedule.eventFrom;
};

const normalizeIdentityPart = (value?: string | null) => (value ?? '').trim().toLowerCase();

/**
 * Keys used to replace an older suggestion when a new plan run returns the same
 * routine/todo (or same meal/sleep/workout slot). Prevents discarded+pending twins.
 */
export const getAllPlanScheduleReplacementKeys = (schedule: ScheduledAction): string[] => {
  const keys: string[] = [];
  const routineId = schedule.routineFrom?.routineActivityId;
  const taskId = schedule.todoFrom?.taskId;
  if (routineId) {
    keys.push(`routine:${routineId}`);
  }
  if (taskId) {
    keys.push(`todo:${taskId}`);
  }
  if (!routineId && !taskId) {
    keys.push(
      [
        'slot',
        schedule.eventFrom ?? '',
        normalizeIdentityPart(schedule.name),
        normalizeIdentityPart(schedule.startDate),
        normalizeIdentityPart(schedule.endDate),
      ].join('|')
    );
  }
  return keys;
};

export const filterAllPlanSchedulesByTypes = (
  schedules: ScheduledAction[],
  types?: AllPlanScheduleType[]
): ScheduledAction[] => {
  if (!types?.length) {
    return schedules;
  }

  const typeSet = new Set(types);
  return schedules.filter((schedule) =>
    typeSet.has(resolveAllPlanScheduleType(schedule) as AllPlanScheduleType)
  );
};

const shouldReplaceScheduleOnTargetDate = (
  schedule: ScheduledAction,
  replaceTypes?: AllPlanScheduleType[]
): boolean => {
  if (!replaceTypes?.length) {
    return true;
  }
  return replaceTypes.includes(resolveAllPlanScheduleType(schedule) as AllPlanScheduleType);
};

/**
 * Drop suggestions on the target date before a Plan My Day run.
 *
 * When `replaceTypes` is set, only those types are removed so a meal/sleep
 * defaults rerun keeps cached todo/routine/workout cards on screen.
 * Otherwise every suggestion owned by the date is removed: accepted/discarded
 * cards are decisions from the previous run, and keeping them causes a full
 * rerun to render stale cards next to newly streamed suggestions. Other dates
 * stay intact so future/tomorrow plans are not wiped by today's rerun.
 */
export const prepareStateForTargetDateRun = (
  prev: Pick<
    SuggestAllAIPlanState,
    'schedules' | 'acceptedIds' | 'autoAcceptedIds' | 'discardedIds' | 'summary'
  >,
  targetDate: string,
  replaceTypes?: AllPlanScheduleType[]
): Pick<
  SuggestAllAIPlanState,
  'schedules' | 'acceptedIds' | 'autoAcceptedIds' | 'discardedIds' | 'summary'
> => {
  const keptSchedules = prev.schedules.filter((schedule) => {
    if (!scheduleFallsOnDate(schedule, targetDate)) {
      return true;
    }
    return !shouldReplaceScheduleOnTargetDate(schedule, replaceTypes);
  });
  const keptIdSet = collectScheduleIds(keptSchedules);

  return {
    schedules: keptSchedules,
    summary: prev.summary,
    acceptedIds: prev.acceptedIds.filter((id) => keptIdSet.has(id)),
    autoAcceptedIds: prev.autoAcceptedIds.filter((id) => keptIdSet.has(id)),
    discardedIds: prev.discardedIds.filter((id) => keptIdSet.has(id)),
  };
};

/** State to show while resuming an in-flight Plan My Day job after reload.
 * Drops the target date's previous suggestions (they belong to the last
 * finished job) and marks the run loading so the cache hydrate cannot paint
 * that old result over the job still generating. */
export const buildDailyPlanJobRecoveryState = (
  cached: SuggestAllAIPlanState,
  targetDate: string,
  typesToReplace?: AllPlanScheduleType[]
): SuggestAllAIPlanState => {
  const stripped = prepareStateForTargetDateRun(cached, targetDate, typesToReplace);
  const replacesEntireDate = !typesToReplace?.length;
  return {
    ...cached,
    ...stripped,
    // A full rerun's summary describes the previous job. Scoped reruns keep
    // it because the kept cards are still on screen.
    summary: replacesEntireDate ? null : stripped.summary,
    isLoading: true,
    statusLines: [],
    error: null,
    targetDate,
  };
};

/**
 * Merge a new Plan My Day run onto kept cross-date state. Drops older suggestions
 * that share routine/todo identity with an incoming item, then dedupes.
 * When `replaceTypes` is set, incoming items outside those types are ignored so
 * a scoped run cannot identity-replace preserved todo/routine/workout cards.
 */
export const appendTargetDatePlanRunResult = (
  base: Pick<SuggestAllAIPlanState, 'schedules' | 'summary'>,
  newSchedules: ScheduledAction[],
  summary: string | null,
  replaceTypes?: AllPlanScheduleType[]
): Pick<SuggestAllAIPlanState, 'schedules' | 'summary'> => {
  const incoming = filterAllPlanSchedulesByTypes(newSchedules, replaceTypes);
  const replacedKeys = new Set(incoming.flatMap(getAllPlanScheduleReplacementKeys));
  const preserved = base.schedules.filter(
    (schedule) =>
      !getAllPlanScheduleReplacementKeys(schedule).some((key) => replacedKeys.has(key))
  );

  return {
    schedules: dedupeAllPlanScheduledActions([...preserved, ...incoming]),
    summary: summary ?? base.summary,
  };
};

/** @deprecated Prefer buildPlanCacheSnapshotForDate from allPlanCache.helpers. */
export const buildTodayCacheSnapshot = (
  state: Pick<
    SuggestAllAIPlanState,
    'schedules' | 'acceptedIds' | 'autoAcceptedIds' | 'discardedIds' | 'summary'
  >,
  todayKey: string
) => {
  const schedules = state.schedules.filter((schedule) => scheduleFallsOnDate(schedule, todayKey));
  const scheduleIds = collectScheduleIds(schedules);

  return {
    schedules,
    summary: state.summary,
    acceptedIds: state.acceptedIds.filter((id) => scheduleIds.has(id)),
    autoAcceptedIds: state.autoAcceptedIds.filter((id) => scheduleIds.has(id)),
    discardedIds: state.discardedIds.filter((id) => scheduleIds.has(id)),
  };
};

export const findWorkoutRoutineActivity = (
  activities?: RoutineActivity[] | null
): RoutineActivity | undefined => {
  return activities?.find((activity) => {
    if (activity.routineActivityId === 'default_workout') {
      return true;
    }
    if (activity.default && activity.category?.id === 'default_exercise') {
      return true;
    }
    return activity.name?.toLowerCase() === 'workout';
  });
};

const getAllPlanScheduleId = (schedule: ScheduledAction, fallbackIndex = 0): string =>
  String(schedule.id ?? `plan-${fallbackIndex}`);

export const shouldDiscardCurrentAllPlanSuggestionsForDate = (date?: string): boolean => {
  if (!date) {
    return true;
  }

  const parsedDate = dayjs(date);
  return parsedDate.isValid() && parsedDate.isSame(dayjs(), 'day');
};

const isWorkoutRichContext = (value: unknown): boolean => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const richContext = value as Record<string, any>;
  const content = richContext.content;
  const payload = content?.payload;

  return (
    content?.type === 'workout_plan' ||
    Boolean(
      payload &&
      typeof payload === 'object' &&
      ('workout_type' in payload || 'exercises' in payload || 'plan_title' in payload)
    )
  );
};

const ctaHasWorkoutSchedule = (cta: unknown): boolean => {
  if (!cta || typeof cta !== 'object' || Array.isArray(cta)) {
    return false;
  }

  const schedule = (cta as Record<string, any>).schedule;
  if (!Array.isArray(schedule)) {
    return false;
  }

  return schedule.some((change) => {
    const after = change?.after;
    const before = change?.before;
    return after?.eventFrom === 'workout' || before?.eventFrom === 'workout';
  });
};

export const hasWorkoutAllPlanReplacementResult = (
  formattedResult: unknown,
  adjustmentRichContexts?: unknown[]
): boolean => {
  const result = formattedResult as Record<string, any> | null;
  const ctas = Array.isArray(result?.ctas) ? result?.ctas : result?.cta ? [result.cta] : [];

  return (
    ctas.some(ctaHasWorkoutSchedule) ||
    ctas.some((cta) => isWorkoutRichContext(cta?.richContext)) ||
    adjustmentRichContexts?.some((entry: any) => isWorkoutRichContext(entry?.richContext)) === true
  );
};

const collectPendingIdsByType = (
  schedules: ScheduledAction[],
  acceptedIds: string[],
  discardedIds: string[],
  scheduleTypes: AllPlanScheduleType[]
): string[] => {
  const targetTypes = new Set(scheduleTypes);

  return schedules.reduce((ids, schedule, index) => {
    const id = getAllPlanScheduleId(schedule, index);
    if (acceptedIds.includes(id) || discardedIds.includes(id)) {
      return ids;
    }

    if (targetTypes.has(resolveAllPlanScheduleType(schedule) as AllPlanScheduleType)) {
      ids.push(id);
    }

    return ids;
  }, [] as string[]);
};

export const discardPendingAllPlanSuggestionsByType = (
  state: SuggestAllAIPlanState,
  scheduleTypes: AllPlanScheduleType[]
): SuggestAllAIPlanState => {
  const nextDiscardedIds = collectPendingIdsByType(
    state.schedules,
    state.acceptedIds,
    state.discardedIds,
    scheduleTypes
  );

  if (nextDiscardedIds.length === 0) {
    return state;
  }

  return {
    ...state,
    discardedIds: Array.from(new Set([...state.discardedIds, ...nextDiscardedIds])),
    autoAcceptedIds: state.autoAcceptedIds.filter((id) => !nextDiscardedIds.includes(id)),
  };
};

export const persistDiscardedPendingAllPlanSuggestionsByType = (
  scheduleTypes: AllPlanScheduleType[],
  date = dayjs().format('YYYY-MM-DD')
): void => {
  const dateKey = resolveAllPlanCacheDateKey(date);
  void updatePlanCacheForDate(dateKey, (existing) => {
    if (!existing || !Array.isArray(existing.schedules)) {
      return null;
    }

    const acceptedIds = Array.isArray(existing.acceptedIds) ? existing.acceptedIds : [];
    const discardedIds = Array.isArray(existing.discardedIds) ? existing.discardedIds : [];
    const nextDiscardedIds = collectPendingIdsByType(
      existing.schedules as ScheduledAction[],
      acceptedIds,
      discardedIds,
      scheduleTypes
    );

    if (nextDiscardedIds.length === 0) {
      return null;
    }

    return {
      ...existing,
      discardedIds: Array.from(new Set([...discardedIds, ...nextDiscardedIds])),
      autoAcceptedIds: Array.isArray(existing.autoAcceptedIds)
        ? existing.autoAcceptedIds.filter((id: string) => !nextDiscardedIds.includes(id))
        : [],
    };
  });
};
